<template>
  <div>
    <v-modal id="copy-permissions-modal" :start-closed="false" @modalClosed="$emit('modalClosed')">
      <span slot="header"> Copia Permessi </span>

      <div class="row-fluid my-3">
        <div class="span6">
          Origine
          <br />
          <v-select2 v-model="fromBusinessUnitId" :options-list="businessUnitsOptions" />
          <br />
          <v-select2 v-model="fromRole" :options-list="rolesOptions" />
        </div>
        <div class="span6">
          Destinazione
          <br />
          <v-select2 v-model="toBusinessUnitId" :options-list="businessUnitsOptions" />
          <br />
          <v-select2 v-model="toRole" :options-list="rolesOptions" />
        </div>
      </div>

      <div class="row-fluid mb-6">
        <div class="span12">
          Copia solo le seguenti risorse <small>(opzionale)</small>
          <br />

          <v-select2
            v-model="selectedResources"
            :options-list="resourcesOptions"
            :multiple="true"
            :data-allow-clear="true"
            :class="'full-width'"
          />
        </div>
      </div>

      <div class="row-fluid my-3">
        <div class="span12">
          <div class="mb-3">
            <h4 v-html="destinationDescription"></h4>
          </div>

          <div v-if="errorMessage" class="cmp-error alert-error">{{ errorMessage }}</div>
          <spinner v-else-if="isLoading" :visible="true"></spinner>
          <copy-previewer
            v-else-if="Object.keys(previewData).length"
            :resourcesList="resourcesList"
            :resourcesExtended="resourcesExtended"
            :previewDiffs="previewDiffs"
          />
          <div v-else>Origine e destinazione devono essere diversi</div>
        </div>
      </div>

      <span slot="footer">
        <a @click="submitCopy" :disabled="!formHasChanges" class="btn btn-success">copia</a>
      </span>
    </v-modal>
  </div>
</template>
<script>
import apiConnector from '@/epersonam/apiConnector';
import { ePersonamApiConnector } from '../../../starApiConnector';
import copyPreviewer from './copyPreviewer';
import vModal from '../../vModal';
import vSelect2 from '../../vSelect2';
import spinner from '../../spinner';

export default {
  name: 'copyPermissionsModal',
  components: {
    vModal,
    vSelect2,
    copyPreviewer,
    spinner,
  },
  props: {
    resources: { type: Object, required: {} },
    currentResource: { type: String },
    roles: { type: Object, required: {} },
  },
  data() {
    const rolesOptions = Object.keys(this.roles).map((role) => [this.roles[role].description[window.locale], role]);
    return {
      isLoading: false,
      errorMessage: '',
      rolesOptions: [['(per tutti i ruoli)', 'all'], ...rolesOptions],
      businessUnitsOptions: [],
      fromRole: 'all',
      fromBusinessUnitId: window.config.business_unit.id,
      toRole: 'all',
      toBusinessUnitId: window.config.business_unit.id,
      selectedResources: [],
      previewData: {},
    };
  },
  watch: {
    formChangesString() {
      if (!this.formHasChanges) {
        this.previewData = {};
        return;
      }
      this.isLoading = true;
      this.errorMessage = '';
      apiConnector()
        .get(`/${window.locale}/permissions/diffs`, {
          params: {
            from_business_unit_id: this.fromBusinessUnitId,
            from_role: this.fromRole == 'all' ? null : this.fromRole,
            to_business_unit_id: this.toBusinessUnitId,
            to_role: this.toRole == 'all' ? null : this.toRole,
          },
        })
        .then(({ data }) => {
          this.isLoading = false;
          this.previewData = data;
        })
        .catch((error) => {
          this.isLoading = false;
          this.errorMessage = i18nJS.errors.critical_error;
          console.log(error);
        });
    },
  },
  computed: {
    i18nJS: () => window.i18nJS,
    resourcesList() {
      return this.selectedResources?.length ? this.selectedResources : this.orderedResources;
    },
    resourcesExtended() {
      const newResources = {};
      Object.keys(this.resources).forEach((k) => {
        // eg. 'Service::HR::DomesticPresencesSynchronization' => descriptionWithPath = 'HR - Sincronizzazione presenze domiciliare (Servizio HR)'
        const path = k.replace(/^Service::/, '').split('::');
        newResources[k] = {
          ...this.resources[k],
          descriptionWithPath: [path.length > 1 ? path[0] : '', this.resources[k].description].filter((f) => f).join(' - '),
        };
      });
      return newResources;
    },
    orderedResources() {
      return Object.keys(this.resourcesExtended).sort((a, b) => {
        return this.resourcesExtended[a].descriptionWithPath.localeCompare(this.resourcesExtended[b].descriptionWithPath);
      });
    },
    resourcesOptions() {
      return this.orderedResources.map((k) => [this.resources[k].description, k]);
    },
    destinationDescription() {
      const roleName = this.rolesOptions.find((o) => o[1] == this.toRole);
      const buName = this.businessUnitsOptions.find((o) => o[1] == this.toBusinessUnitId);
      if (!roleName || !buName) return '';
      return `Anteprima permessi in <strong style="color: #EF7B12;">${buName[0]}</strong> per <strong style="color: #EF7B12;">${roleName[0]}</strong>`;
    },
    // This method is used to track if changes have been made when dropdowns are used.
    // Returns eg. '851:inf>851:coord'
    formChangesString() {
      return `${this.fromBusinessUnitId}:${this.fromRole}>${this.toBusinessUnitId}:${this.toRole}`;
    },
    // Returns boolean
    formHasChanges() {
      return this.fromBusinessUnitId != this.toBusinessUnitId || this.fromRole != this.toRole;
    },
    // Given `this.resourcesExtended` as eg.
    // {
    //   "ConfirmedTherapy": {
    //     "actions": {"index": "Indice","update": "Aggiornamento","create": "Creazione","print": "Stampa"},
    //   },
    //   ...
    // }
    // And `this.previewData` as eg.
    // {
    //   "ConfirmedTherapy": {
    //     "-": ["destroy"],                              // permissions removed
    //     "+": ["create", "edit"],                       // added
    //     "=": ["index", "print", "recepit"]             // unchanged
    //   },
    //   ...
    // }
    // Returns eg.
    // {
    //   "UserBuAbsence": {
    //     "permit": [{ diff: ['action1'], same: [...]}],
    //     "forbid": [{ diff: ['action1'], same: [...]}]
    //   },
    //   ...
    // }
    previewDiffs() {
      return this.orderedResources.reduce((obj, key) => {
        if (!this.previewData) {
          console.error(`Missing permission key ${key}!`);
          return obj;
        }
        const permittedActions = [...this.previewData[key]['+'], ...this.previewData[key]['=']];
        const deniedActions = Object.keys(this.resourcesExtended[key].actions)
          .filter((action) => !this.previewData[key]['-'].includes(action))
          .filter((action) => !permittedActions.includes(action));

        return {
          ...obj,
          [key]: {
            permit: {
              diff: this.previewData[key]['+'],
              same: this.previewData[key]['='],
            },
            forbid: {
              diff: this.previewData[key]['-'],
              same: deniedActions,
            },
          },
        };
      }, {});
    },
  },
  mounted() {
    this.isLoading = true;
    this.errorMessage = '';
    ePersonamApiConnector()
      .get(`/private/v1/business_units.json`)
      .then(({ data }) => {
        this.isLoading = false;
        this.businessUnitsOptions = data.filter((bu) => bu.structure).map((bu) => [bu.name, bu.id]);
      })
      .catch((error) => {
        this.isLoading = false;
        this.errorMessage = i18nJS.errors.critical_error;
        console.log(error);
      });
  },
  methods: {
    submitCopy() {
      this.isLoading = true;
      this.errorMessage = '';
      const resources = this.selectedResources.length ? this.selectedResources : Object.keys(this.previewDiffs);
      apiConnector()
        .post(`${window.locale}/permissions/copy`, {
          permissions: resources.map((key) => {
            const diffs = this.previewDiffs[key];
            return {
              permit: [...diffs.permit.diff, ...diffs.permit.same],
              forbid: [...diffs.forbid.diff, ...diffs.forbid.same],
              business_unit_id: this.toBusinessUnitId,
              resource: key,
              role_code: this.toRole == 'all' ? null : this.toRole,
            };
          }, {}),
        })
        .then(({ data }) => {
          this.isLoading = false;
          location.reload();
        })
        .catch((error) => {
          this.isLoading = false;
          this.errorMessage = i18nJS.errors.critical_error;
          console.log(error);
        });
    },
  },
};
</script>
<style scoped>
.my-3 {
  margin: 20px 0;
}
.mb-3 {
  margin-bottom: 10px;
}
.cmp-error {
  padding: 10px;
}
</style>
